import { call, put, takeEvery, takeLatest } from "redux-saga/effects"
import { actions, actionTypes, authTriggerActions } from "../store/reducers/authReducer"
import { RegUser, SagasData } from "../../types/types"
import { updateToken } from "../../utils/updateToken"
import { authAPI, workspaceAPI, supportAccessAPI, passwordAPI } from "../api/api"
import { IUser, IWorkspace } from "../../types/reducers/authReducer"
import { ILoginUser, IMagicLoginUser } from "../../types/sagas"
import { generalActions } from "../store/reducers/generalReducer"
import { SagaWrapper } from './index';
import { billingActions as billingActionCreators, billingTriggerActions } from "../store/actions/creators/billingActions";
import { hasPermission } from "../../utils/permissions";
import { isWelcomeSplashDismissedLocally } from "../../utils/welcomeSplashDismissal";
import { listQueue } from "../../offline/queue";
import { isOfflineFeatureEnabled, setOfflineFeatureEnabled } from "../../offline/featureFlag";
import { unregisterServiceWorker } from "../../serviceWorkerRegistration";
import { isOnline } from "../../offline/isOnline";
import i18n from "../../res/languages";
import { onboardingRedirectFor } from "./onboardingRedirect";
import { clearAllReportDrafts } from "../../next/visits/draft";
import { inAppPath } from "../../utils/inAppPath";
import localforage from "localforage";
import { setPreviewUser, clearPreviewUser } from "../../next/preview/previewFlag";
import { setLegacyView } from "../../next/preview/legacyView";
import { isPublicNext } from "../../next/public/mode";
import { signInFailure } from "../../next/public/signInErrors";
import { lockedOf } from "../../next/bridges/locked";

/** How many offline writes are still waiting to reach the server. */
export function* countUnsyncedWrites(): any {
  try {
    const rows: any[] = yield call(listQueue);
    return rows.length;
  } catch {
    // A queue we cannot read must never be able to block a logout.
    return 0;
  }
}

function* getToken() {
  try {
    const response: { status: number } = yield authAPI.getToken()
    if (response.status === 204 || response.status === 304) {
      updateToken()
    }
  } catch (error) {
    console.log(error)
  }
}

function* regUser(data: SagasData<RegUser>) {
  yield put({ type: actionTypes.REG_USER_START })
  try {
    const response: {
      status: number
      data: {
        user: IUser
      }
    } = yield authAPI.regUser(data.data)
    if (response.status === 200) {
      // Registration auto-approves and logs the user in on the backend, so drop them
      // straight into the app (mirrors loginUser) instead of showing a pending message.
      updateToken()
      localStorage.setItem("user", "1")
      yield put(actions.setUser(response.data.user))
      yield put(actions.loginUserAC(response.data.user))
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "An error occurred",
        type: "error",
      })
    )
  } finally {
    yield put({ type: actionTypes.REG_USER_END })
  }
}

function* loginUser(payload: ILoginUser) {
  try {
    const response: {
      status: number
      data: {
        user: IUser
      }
    } = yield authAPI.loginUser(payload.data)

    if (response.status === 200) {
      updateToken()
      localStorage.setItem("user", "1")
      yield put(actions.setUser(response.data.user))
      yield put(actions.loginUserAC(response.data.user))

      // A user who started the new onboarding flow and never finished it gets sent
      // straight back in, instead of landing in the app like a normal sign-in.
      const onboardingRedirect = onboardingRedirectFor(response.data.user)
      if (onboardingRedirect) {
        window.location.href = onboardingRedirect
      }
    }
  } catch (error: any) {
    if (error.response && error.response.status === 419) {
      document.cookie.split(";").forEach(function (cookie) {
        document.cookie = cookie
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      // document.cookie = "connectyid_session=;expires=" + new Date().toUTCString() + ";path=/";
    }
    // Public v3 (P2.3): the new sign-in page words "no match" and "too many tries" itself, on the form, instead of a pop-up
    // carrying the server's English sentence.
    const failure = isPublicNext() ? signInFailure(error) : null
    if (failure) {
      yield put({ type: actionTypes.SET_LOGIN_ERROR, payload: failure })
      return
    }
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "An error occurred",
        type: "error",
      })
    )
  }
}

/** Public v3 (I2): "Continue as X" signs out normally and then uses this link; kept across the unsynced-writes question. */
let continueWithLink: any = null

export function* logoutUser(action?: any) {
  // A new sign-out starts clean; only the "sign out anyway" answer (force) keeps the link that was waiting.
  continueWithLink = action?.then ?? (action?.force ? continueWithLink : null)
  try {
    // Signing out is a write, and writeGuard rejects every write at the transport
    // while offline for a flagged user — so this request cannot succeed. Refuse
    // here instead, with a message that names the reason rather than the generic
    // "this change needs a connection".
    //
    // The condition mirrors shouldBlockWrite exactly so the two can never disagree
    // about whether a sign-out is possible.
    //
    // Placed ABOVE the unsynced-writes confirmation on purpose: otherwise a
    // volunteer with queued visit reports is asked to confirm abandoning them,
    // confirms, and only then hits the offline wall — a frightening prompt for an
    // action that was never going to happen.
    if (!isOnline() && isOfflineFeatureEnabled()) {
      yield put(
        generalActions.errorAC({
          message: i18n.t("offline.logout_needs_connection"),
          type: "error",
        })
      )
      return
    }

    // A queued write belongs to the user who made it: it replays with their token,
    // against their workspace. Logging out strands it — it would sit in IndexedDB
    // until the same user signs back in on this device, and would be wrong to replay
    // for anyone else. So the user is told what is unsynced and has to confirm.
    // `force` is what the confirmation dialog passes back.
    if (!action?.force) {
      const pending: number = yield call(countUnsyncedWrites)
      if (pending > 0) {
        yield put(authTriggerActions.setPendingLogoutWarningAC(pending))
        return
      }
    }

    const response: DataType = yield authAPI.logoutUser()
    if (response.status === 204) {
      localStorage.removeItem("user")
      // The new design's visit-report drafts belong to this user: none stays on the device (2D F5).
      clearAllReportDrafts()
      // The saved copy of routes / contacts / reports belongs to this user too (redux-persist, key
      // "connectyid"): the next person on this device must not see it.
      try { yield call([localforage, localforage.removeItem], "persist:connectyid") } catch { /* storage unavailable */ }
      localStorage.removeItem("workspace_locked")
      clearPreviewUser()
      setLegacyView(false)
      localStorage.removeItem("admin_original_token")
      // The offline flag is per-user, so it must not outlive the session: the next
      // person to reach /login on this device (or the same person before signing
      // back in) would otherwise still pass isOfflineFeatureEnabled() and set the
      // boot-time background work running against a dead token.
      setOfflineFeatureEnabled(false)
      sessionStorage.removeItem("smartBillingWizardState")
      sessionStorage.removeItem("setupChoiceDismissed")
      sessionStorage.removeItem("adminContact")
      sessionStorage.removeItem("_setupReturnPolling")
      sessionStorage.removeItem("_subscriptionReturnPolling")
      sessionStorage.removeItem("countryNudgeDismissed")
      updateToken()
      yield put(actions.setUser(null))
      yield put(actions.loginUserAC(null))
      if (continueWithLink) {
        const link = continueWithLink
        continueWithLink = null
        yield put(authTriggerActions.tokenLoginUserTriggerAC(link))
        return
      }
      window.location.href = "/login"
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "An error occurred",
        type: "error",
      })
    )
  }
}


function* getTokenForSocketConnection({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: 'post',
    endpoint: "/broadcasting/auth",
    body: {
      socket_id: action.id.id,
      channel_name: "private-admin.124"
      // channel_name: action.payload.channelName
    }
  })

  dispatchResponse(
    { data, error },
    actionTypes.GET_TOKEN_FOR_SOCKET_CONNECTION_SUCCESS,
    actionTypes.GET_TOKEN_FOR_SOCKET_CONNECTION_ERROR
  )
}

// IMPORTANT: getUser must use authAPI.getMe() (which goes through the `instance`
// Axios client with the Bearer token interceptor) rather than ApiService/SagaWrapper.
// ApiService uses a separate plain axios instance that has NO auth interceptors and
// sends NO Authorization header, so it falls back to session-cookie authentication.
// During impersonation, the session cookie still belongs to the admin — causing /me
// to return the admin's data instead of the impersonated user's data.
function* getUser(action?: any) {
  try {
    const response: { status: number; data: any } = yield call(authAPI.getMe);
    const { status, data } = response;

    if (status === 200 && data && data.user) {
      if (data.user.language) {
        localStorage.setItem('language', data.user.language)
      }

      // Mirror the server's per-user offline flag into localStorage: the decisions it
      // gates (registering the service worker, persisting the store) happen at boot,
      // before /me resolves, so they read the value this call left behind. Turning a
      // user OFF also tears down anything already installed on their device — that is
      // the rollback path for a service worker, which a plain deploy cannot reach.
      const offlineEnabled = Boolean(data.offline_enabled)
      const wasEnabled = isOfflineFeatureEnabled()
      setOfflineFeatureEnabled(offlineEnabled)
      if (wasEnabled && !offlineEnabled) {
        yield call(unregisterServiceWorker)
      }
      // /me reports impersonation at the top level, next to `user`; the app reads it as
      // currentUser.is_impersonating (new frame, admin menu hiding, start-route buttons).
      // Preview mode: set before the user lands in the store, so the switchboard already answers for this person on the next render.
      setPreviewUser(Boolean(data.preview))
      // "Show me the old version": the server says it only for staff who turned it on.
      setLegacyView(Boolean(data.legacy_view))
      yield put(actions.setUser({ ...data.user, is_impersonating: Boolean(data.is_impersonating), preview: Boolean(data.preview) }))

      // GET_USER is dispatched from far more places than sign-in: billingSaga
      // refreshes the user after a managed-upload success and after a setup
      // purchase, and SmartBillingWizard polls it every couple of seconds while
      // waiting on setup-fee confirmation. None of those want a navigation side
      // effect — yanking someone out of an active payment flow is exactly the
      // harm this feature exists to prevent. So the redirect is opt-in: only the
      // two genuine sign-in entry points (App's boot dispatch, and
      // loginUserWithToken's GET_USER for the magic-link path — see its own
      // comment for why it defers to a fresh /me instead of trusting its own
      // response) set checkOnboarding on the action. Every other caller keeps
      // refreshing the user with no navigation, which is what it actually wants.
      if (action?.checkOnboarding) {
        // Guarded against redirect loops: GET_USER also fires while the
        // onboarding page itself is loading, so skip when already there.
        const onboardingRedirect = onboardingRedirectFor(data.user)
        if (onboardingRedirect && !window.location.pathname.startsWith(onboardingRedirect)) {
          window.location.href = onboardingRedirect
          return
        }
      }

      // Ingest workspace data — parse decimal strings to numbers at the boundary
      if (data.workspace) {
        const ws = data.workspace;
        // Persist the workspace country so the Google Maps JS loader can pick the
        // right language/region SYNCHRONOUSLY at the next boot (it mounts before /me
        // resolves). Cleared when absent so a country change can't get stuck.
        if (ws.country_code) {
          localStorage.setItem('workspace_country_code', String(ws.country_code));
        } else {
          localStorage.removeItem('workspace_country_code');
        }
        const workspace: IWorkspace = {
          id: ws.id,
          name: ws.name,
          country_code: ws.country_code ?? null,
          country_name: ws.country_name ?? null,
          default_departure_address: ws.default_departure_address ?? null,
          default_departure_lat: ws.default_departure_lat != null ? parseFloat(ws.default_departure_lat) : null,
          default_departure_lng: ws.default_departure_lng != null ? parseFloat(ws.default_departure_lng) : null,
          default_departure_place_id: ws.default_departure_place_id ?? null,
          default_departure_street_name: ws.default_departure_street_name ?? null,
          default_departure_city: ws.default_departure_city ?? null,
          default_departure_state: ws.default_departure_state ?? null,
          default_departure_country: ws.default_departure_country ?? null,
          default_departure_zip_code: ws.default_departure_zip_code ?? null,
          map_center_lat: ws.map_center_lat != null ? parseFloat(ws.map_center_lat) : null,
          map_center_lng: ws.map_center_lng != null ? parseFloat(ws.map_center_lng) : null,
          map_center_zoom: ws.map_center_zoom != null ? Number(ws.map_center_zoom) : null,
          map_center_name: ws.map_center_name ?? null,
          include_manual_in_optimize: ws.include_manual_in_optimize ?? true,
          manual_addresses_enabled: ws.manual_addresses_enabled ?? true,
          manual_default_region_name: ws.manual_default_region_name ?? null,
          manual_default_state: ws.manual_default_state ?? null,
          manual_default_country: ws.manual_default_country ?? null,
          auto_assign_default_category: ws.auto_assign_default_category ?? true,
          address_settings_split: ws.address_settings_split ?? false,
          // Volunteering nearby-centre search (volunteering-nearby-backend-report.md):
          // derived server-side (default_departure_lat/lng, falling back to
          // map_center_lat/lng), so this explicit allowlist must carry them
          // through the same way every other coordinate field above already is,
          // or NearbyCentres silently falls back to "no address on the account"
          // for every workspace that actually has one.
          address_lat: ws.address_lat != null ? parseFloat(ws.address_lat) : null,
          address_lng: ws.address_lng != null ? parseFloat(ws.address_lng) : null,
        };
        yield put({ type: actionTypes.SET_WORKSPACE_FROM_AUTH, payload: workspace });
      }

      // Sync current project id into projectsStore
      if (data.current_project_id !== undefined) {
        yield put({ type: "SET_CURRENT_PROJECT_SUCCESS", payload: data.current_project_id ?? null });
      }

      // Fetch projects (used by the map header chip + filter panel) only for users who can
      // see the Map. Gating on read_map matches map access exactly, and every read_map
      // holder also has read_routes, which the /projects endpoint requires — so this avoids
      // the spurious 403 error toast a limited user without map access would otherwise get.
      // A locked account (trial over, no plan) answers /projects with 402 on every page: skip it there.
      const accountLocked = lockedOf({
        billingEnabled: data.billing_enabled,
        hasActiveSubscription: data.billing?.has_active_subscription,
        isTrialActive: data.billing?.is_trial_active,
        trialEndsAt: data.billing?.trial_ends_at,
      }, data.user?.role === "super_admin");
      if (hasPermission("read_map", data.user) && !accountLocked) {
        yield put({ type: "FETCH_PROJECTS" });
      }

      // Always dispatch top-level fields (independent of billing object)
      yield put(billingActionCreators.setBillingFromAuth({
        billingEnabled: data.billing_enabled ?? false,
        adminContact: data.admin_contact ?? null,
        isWorkspaceOwner: data.is_workspace_owner ?? false,
      }));

      // Extract billing data if present in the /api/auth/me response
      if (data.billing) {
        const billing = data.billing;
        yield put(billingActionCreators.setBillingFromAuth({
          trialDays: billing.trial_days,
          trialEndsAt: billing.trial_ends_at,
          isTrialActive: billing.is_trial_active,
          trialRemainingHours: billing.trial_remaining_hours,
          subscriptionStatus: billing.subscription_status,
          hasActiveSubscription: billing.has_active_subscription,
          currentPlan: billing.current_plan,
          contactCount: billing.contact_count,
          contactLimit: billing.contact_limit,
          recommendedTier: billing.recommended_tier,
          welcomeSplashSeen: billing.welcome_splash_seen,
          isNewUser: billing.is_new_user,
          managedUploadRequested: billing.managed_upload_requested,
          managedUploadFileUploaded: billing.managed_upload_file_uploaded,
          managedUploadStatus: billing.managed_upload_status,
          isSuperAdminWorkspace: billing.is_super_admin_workspace,
          overContactLimit: billing.over_contact_limit ?? false,
          contactLimitOver: billing.contact_limit_over ?? false,
          contactLimitGraceUntil: billing.contact_limit_grace_until ?? null,
          // Older backends omit these: leave the store at its default ("active") then.
          ...(billing.account_state !== undefined && {
            accountState: billing.account_state,
            closesAt: billing.closes_at ?? null,
            deleteAfter: billing.delete_after ?? null,
          }),
        }));

        yield* maybeRetryWelcomeSplashDismissal(data.workspace?.id, billing.welcome_splash_seen);
      }
    }
  } catch (error: any) {
    // 401 errors are handled globally by the Axios response interceptor;
    // other failures (network errors, etc.) are silently swallowed here.
  }
}
function* updateWorkspaceSettings({ payload }: any) {
  try {
    const response: { status: number; data: any } = yield call(workspaceAPI.updateSettings, payload);
    if (response.status === 200) {
      const ws = response.data.data ?? response.data;
      // Keep the localStorage mirror in step with the store. Only the /me path used to
      // write it, so changing the country in Settings left the mirror stale until the next
      // reload — and the mirror is what the Google Maps loader reads synchronously at boot
      // (see geocodeLocale.ts) and what the phone fields fall back to for their default
      // country (usePhoneDefaultCountry).
      if (ws.country_code) {
        localStorage.setItem('workspace_country_code', String(ws.country_code));
      } else {
        localStorage.removeItem('workspace_country_code');
      }
      const workspace: IWorkspace = {
        id: ws.id,
        name: ws.name,
        country_code: ws.country_code ?? null,
        country_name: ws.country_name ?? null,
        default_departure_address: ws.default_departure_address ?? null,
        default_departure_lat: ws.default_departure_lat != null ? parseFloat(ws.default_departure_lat) : null,
        default_departure_lng: ws.default_departure_lng != null ? parseFloat(ws.default_departure_lng) : null,
        default_departure_place_id: ws.default_departure_place_id ?? null,
        default_departure_street_name: ws.default_departure_street_name ?? null,
        default_departure_city: ws.default_departure_city ?? null,
        default_departure_state: ws.default_departure_state ?? null,
        default_departure_country: ws.default_departure_country ?? null,
        default_departure_zip_code: ws.default_departure_zip_code ?? null,
        map_center_lat: ws.map_center_lat != null ? parseFloat(ws.map_center_lat) : null,
        map_center_lng: ws.map_center_lng != null ? parseFloat(ws.map_center_lng) : null,
        map_center_zoom: ws.map_center_zoom != null ? Number(ws.map_center_zoom) : null,
        map_center_name: ws.map_center_name ?? null,
        include_manual_in_optimize: ws.include_manual_in_optimize ?? true,
        manual_addresses_enabled: ws.manual_addresses_enabled ?? true,
        manual_default_region_name: ws.manual_default_region_name ?? null,
        manual_default_state: ws.manual_default_state ?? null,
        manual_default_country: ws.manual_default_country ?? null,
        auto_assign_default_category: ws.auto_assign_default_category ?? true,
        address_settings_split: ws.address_settings_split ?? false,
        // Same reasoning as the /me ingestion above -- an address saved from
        // Settings must update NearbyCentres' picture of the workspace's
        // address immediately, not only after the next full /me refetch.
        address_lat: ws.address_lat != null ? parseFloat(ws.address_lat) : null,
        address_lng: ws.address_lng != null ? parseFloat(ws.address_lng) : null,
      };
      yield put({ type: actionTypes.UPDATE_WORKSPACE_SETTINGS_SUCCESS, payload: workspace });
      yield put(generalActions.successAC({ message: "Workspace settings saved", type: "success" }));
    }
  } catch (error: any) {
    yield put({ type: actionTypes.UPDATE_WORKSPACE_SETTINGS_ERROR });
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "Failed to save workspace settings",
        type: "error",
      })
    );
  }
}

function* updateSupportAccess({ payload }: any) {
  try {
    const response: { status: number; data: any } = yield call(supportAccessAPI.updateSupportAccess, payload);
    if (response.status === 200) {
      const data = response.data?.data ?? response.data;
      const approved: boolean = data?.support_access_approved ?? payload.support_access_approved;
      yield put({ type: actionTypes.UPDATE_SUPPORT_ACCESS_SUCCESS, payload: approved });
      yield put(
        generalActions.successAC({
          message: approved ? "Support access allowed" : "Support access revoked",
          type: "success",
        })
      );
    }
  } catch (error: any) {
    yield put({ type: actionTypes.UPDATE_SUPPORT_ACCESS_ERROR });
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "Failed to update support access",
        type: "error",
      })
    );
  }
}

function* updatePassword({ payload }: any) {
  try {
    const response: { status: number; data: any } = yield call(passwordAPI.updatePassword, payload);
    if (response.status === 200) {
      yield put({ type: actionTypes.UPDATE_PASSWORD_SUCCESS });
      yield put(generalActions.successAC({ message: "Password saved", type: "success" }));
    }
  } catch (error: any) {
    yield put({
      type: actionTypes.UPDATE_PASSWORD_ERROR,
      payload: error.response?.data?.errors?.password?.[0] || error.response?.data?.message || "Failed to save password",
    });
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.errors?.password?.[0] || error.response?.data?.message || "Failed to save password",
        type: "error",
      })
    );
  }
}

// GET_USER (and therefore GET_USER_SUCCESS) is dispatched from several places —
// loginUserWithToken, and billingSaga.ts after an upload/setup-confirm action —
// so more than one GET_USER_SUCCESS can land in quick succession, each with its
// own should_prompt_set_password: true, before the markPromptShown call below has
// had a chance to reach the backend and get password_prompt_shown_at stamped.
// Without a client-side guard, each one would independently reopen the modal —
// including AFTER the user already dismissed it with "Skip for now" — violating
// the "show at most once ever per user, per session" requirement. A plain
// module-level flag is sufficient (rather than needing e.g. takeLeading): JS is
// single-threaded and the check-and-set below runs synchronously before this
// generator's first yield, so it can't race across separate GET_USER_SUCCESS
// dispatches even under takeEvery. Never reset — it's meant to last the whole
// page-load/session, matching the "at most once per session" requirement.
let hasPromptedSetPasswordThisSession = false;

// Which workspace ids have already had their one dismiss-welcome retry fired
// this browser session. GET_USER is NOT a once-per-app-load event: it also
// fires from SmartBillingWizard's setup-fee confirmation poll (every 2s, up
// to 15x), and from SettingsPage, VolunteeringPage and the contact-limit
// interceptor. DISMISS_WELCOME_TRIGGER is handled with takeLatest, which does
// NOT network-cancel a PUT already in flight when a new one is dispatched —
// it just abandons listening for its result and starts another. Without this
// guard, a leftover local marker would fire a fresh duplicate PUT on every
// single one of those GET_USER calls while an earlier one is still blocked
// behind the very import lock that caused the original bug, stacking up
// concurrent requests against that same lock. A plain module-level Set is
// enough for the same reason hasPromptedSetPasswordThisSession above is: JS
// is single-threaded, so the has()/add() below can't race across dispatches.
// Never reset — matches "at most one retry per workspace per session".
const workspacesRetriedThisSession = new Set<string>();

// Called from getUser (the /me handler) — see workspacesRetriedThisSession
// above for why "called from getUser" does not mean "runs once". The
// dismiss-welcome PUT WelcomeSplash fires on click can fail silently, or the
// page can be closed before it lands (see billingSaga's dismissWelcome
// comment: it can also be held up for a long time, blocked behind a
// contact-import lock on the workspace row). If localStorage still remembers
// a local dismissal the server hasn't caught up to, keep the splash hidden
// for this session (optimistic DISMISS_WELCOME_SUCCESS, same as the click
// handler) and fire exactly one retry PUT in the background, ever, per
// workspace, per session. billingSaga's dismissWelcome clears the local
// marker once the retry actually succeeds.
export function* maybeRetryWelcomeSplashDismissal(
  workspaceId: number | string | null | undefined,
  welcomeSplashSeen: boolean | undefined
): any {
  if (workspaceId === null || workspaceId === undefined) {
    return;
  }
  const key = String(workspaceId);
  if (
    welcomeSplashSeen === false &&
    !workspacesRetriedThisSession.has(key) &&
    isWelcomeSplashDismissedLocally(workspaceId)
  ) {
    workspacesRetriedThisSession.add(key);
    yield put(billingActionCreators.dismissWelcomeSuccess());
    yield put(billingTriggerActions.dismissWelcome());
  }
}

// Fires after every successful GET_USER_SUCCESS (login, registration, /me).
// When the backend says this user has never been shown the "set a password"
// nudge, marks it shown (fire-and-forget — a failure here must not block the
// user) and opens the modal. Idempotent: once password_prompt_shown_at is
// stamped server-side, subsequent /me calls report should_prompt_set_password
// as false and this becomes a no-op.
export function* maybePromptSetPassword({ payload }: any) {
  if (!payload?.should_prompt_set_password) {
    return;
  }

  // Walkthrough #5: someone not approved (waiting for review, or in no account) cannot open anything yet; the call would be refused.
  if (payload.isApproved === false) {
    return;
  }

  // Staff viewing as the person (5A): the nudge is the person's own. Showing it to staff would let them set the
  // person's password (the server refuses that) and stamping it "shown" would take the nudge away from the person.
  if (payload.is_impersonating) {
    return;
  }

  if (hasPromptedSetPasswordThisSession) {
    return;
  }
  hasPromptedSetPasswordThisSession = true;

  try {
    yield call(passwordAPI.markPromptShown);
  } catch (error) {
    // Best-effort: worst case the nudge reappears once more on a later login.
  }

  yield put({ type: actionTypes.SET_PASSWORD_MODAL_OPEN });
}

function* changeUserLanguage({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: 'post',
    endpoint: "/api/auth/set-user-data",
    body: {
      language: action.language.language,
    }
  })

  yield dispatchResponse(
    { data, error },
    actionTypes.CHANGE_USER_LANGUAGE_SUCCESS,
    actionTypes.CHANGE_USER_LANGUAGE_ERROR
  )
}

export function* forgetPassword({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "POST",
    endpoint: "/api/forgot-password",
    // In the body, never the URL: an address with a "+" or "&" broke in the query and URLs land in logs.
    body: { email: action.data.email },
  })

  yield dispatchResponse(
    { data, error },
    actionTypes.FORGET_PASSWORD_SUCCESS,
    actionTypes.FORGET_PASSWORD_ERROR
  )
}

export function* resetPassword({ request, action, dispatchResponse }: any) {
  const { data, error } = yield request({
    method: "POST",
    endpoint: "/api/reset-password",
    // The new password travels in the body, never in the URL (server and proxy logs keep URLs; "&" or "#" broke it).
    body: {
      email: action.data.email,
      token: action.data.token,
      password: action.data.password,
      password_confirmation: action.data.password_confirmation,
    },
  });

  yield dispatchResponse(
    { data, error },
    actionTypes.RESET_PASSWORD_SUCCESS,
    actionTypes.RESET_PASSWORD_ERROR
  )
}

function* loginUserWithToken(payload: IMagicLoginUser) {
  try {
    const response: {
      status: number;
      data: { user: IUser; token: string };
    } = yield authAPI.loginUserWithToken(payload.data);

    // Stage 7A (F9), a deliberate change to today's sign-in page: a link that was already used or has expired is
    // answered 200 with "a fresh link was e-mailed" and no user. Say so in plain words instead of silently
    // returning to sign-in.
    if (response.status === 200 && !response.data?.user) {
      // Public v3 (P3.1): the new look answers with a page in the frame ("already used" / "has expired"); the address is not
      // read out on it, it only lets "Send me another link" work.
      if (isPublicNext()) {
        const state = (response.data as any)?.link_state === "used" ? "used" : "expired";
        window.location.href = `/login?link=${state}&email=${encodeURIComponent(payload.data.email || "")}`;
        return;
      }
      // (No page load here: it would wipe the sentence.)
      yield put(generalActions.errorAC({ message: i18n.t("first_page.link_used_resent"), type: "info" }));
      return;
    }
    if (response.status === 200) {
      updateToken();
      localStorage.setItem("user", "1");
      // Public v3 (P4.4): remembered for this tab, so "Set a password" can say it was a link.
      try { sessionStorage.setItem("cy_signed_in_by_link", "1"); } catch { /* storage blocked: the other wording applies */ }
      if (payload.data.workspace_id) {
        localStorage.setItem("workspace_locked", "true");
      }
      // Dispatch GET_USER to fetch fresh user data from /me rather than relying
      // on the token-login response alone. This ensures the correct workspace
      // permissions (including scoped route permissions) are loaded before the
      // app navigates, so limited users with route-only access land on
      // /saved-routes instead of /households. checkOnboarding: true is what
      // makes this a genuine sign-in entry point for the onboarding redirect
      // (see getUser) rather than an ordinary refresh.
      // Stage 7A (F9): the e-mail button's `next` — only an in-app path; anything else is ignored. A full page load
      // is enough: the session is set, and the app's boot (checkOnboarding) still sends an unfinished user on.
      const next = inAppPath(payload.data.next);
      if (next) {
        window.location.href = next;
        return;
      }
      yield put({ type: actionTypes.GET_USER, checkOnboarding: true });
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "Login link is invalid or has expired.",
        type: "error",
      })
    );
    window.location.href = "/login?mode=magic";
  }
}

function* sendLoginLink({ email }: any) {
  try {
    const response: {
      status: number;
      data: any;
    } = yield authAPI.sendLoginLink({ email });
    if (response.status === 200) {
      // Public v3 (P3.4): the new page says it on the screen (with what to do next), so no pop-up on top of it.
      if (!isPublicNext()) yield put(
        generalActions.successAC({
          message: "A login link has been sent to your email.",
          type: "success",
        })
      );
      yield put(actions.sendLoginLinkSuccess(response.data));
    }
  } catch (error: any) {
    yield put(
      generalActions.errorAC({
        message: error.response?.data?.message || "Failed to send login link.",
        type: "error",
      })
    );
  }
}



export function* authWatcher() {
  yield takeEvery(actionTypes.FORGET_PASSWORD, SagaWrapper.withRequest(forgetPassword))
  yield takeEvery(actionTypes.RESET_PASSWORD, SagaWrapper.withRequest(resetPassword))
  yield takeEvery(actionTypes.LOGIN_USER_TRIGGER, loginUser)
  yield takeLatest(actionTypes.REG_USER_TRIGGER, regUser)
  yield takeEvery(actionTypes.GET_TOKEN_TRIGGER, getToken)
  yield takeEvery(actionTypes.LOGOUT_TRIGGER, logoutUser)
  yield takeEvery(actionTypes.GET_TOKEN_FOR_SOCKET_CONNECTION, SagaWrapper.withRequest(getTokenForSocketConnection))
  yield takeEvery(actionTypes.GET_USER, getUser)
  yield takeEvery(actionTypes.CHANGE_USER_LANGUAGE, SagaWrapper.withRequest(changeUserLanguage))
  yield takeEvery(actionTypes.TOKEN_LOGIN_USER_TRIGGER, loginUserWithToken)
  yield takeEvery(actionTypes.LINK_LOGIN_USER_TRIGGER, sendLoginLink)
  yield takeLatest(actionTypes.UPDATE_WORKSPACE_SETTINGS, updateWorkspaceSettings)
  yield takeLatest(actionTypes.UPDATE_SUPPORT_ACCESS, updateSupportAccess)
  yield takeLatest(actionTypes.UPDATE_PASSWORD, updatePassword)
  yield takeEvery(actionTypes.GET_USER_SUCCESS, maybePromptSetPassword)
  // yield takeEvery("TEST", testSaga)
}

type DataType = {
  status: number
  data: {
    token: string
  }
}
